import { mulberry32, seedFrom } from "./random.js";
import { COLORS, SHAPES, packToken, tokenColor, tokenShape } from "./sequence.js";
/**
 * Spot the difference — two panels, a few cells changed.
 *
 * Visual attention and change detection: the child compares two arrangements and finds where they
 * disagree. Language-free by construction, and the one activity here where the interesting
 * behaviour is *search* — how many wrong cells were tapped on the way, which separates a child who
 * scanned systematically from one who guessed.
 *
 * **The grid is 3×2 and stays that way.** Two panels have to fit one phone screen with cells a
 * child can hit: stacked vertically at 3×2 the cells come out around 108 px, comfortably past the
 * ~76 px floor. Side by side they would be 56 px, and at 3×3 stacked the second panel would fall
 * off the bottom. Difficulty grows through the number and the subtlety of the changes instead —
 * which is the better axis anyway, since a bigger grid mostly measures patience.
 */
export const DIFF_COLS = 3;
export const DIFF_ROWS = 2;
export const DIFF_CELLS = DIFF_COLS * DIFF_ROWS;
/**
 * Eight levels on a grid that never grows.
 *
 * The grid is fixed at 3×2 for touch reasons (see the note above), so the levels come from the KIND
 * of change rather than the count — four of six cells differing is close to "everything is
 * different", which is easy to see and merely tedious to tap. Levels 6–8 introduce swaps, which is
 * a genuine step in subtlety costing no extra cells.
 */
function recipeFor(difficulty) {
    switch (difficulty) {
        case 1:
            return { count: 1, kinds: ["shape"] };
        case 2:
            return { count: 2, kinds: ["shape", "shape"] };
        case 3:
            return { count: 2, kinds: ["shape", "color"] };
        case 4:
            return { count: 3, kinds: ["shape", "color", "shape"] };
        case 5:
            // Colour only: the same shapes throughout, so nothing can be found by silhouette.
            return { count: 3, kinds: ["color", "color", "color"] };
        case 6:
            return { count: 2, kinds: ["swap"] };
        case 7:
            return { count: 3, kinds: ["swap", "color"] };
        default:
            return { count: 4, kinds: ["swap", "swap"] };
    }
}
export const DIFF_MIN_DIFFICULTY = 1;
export const DIFF_MAX_DIFFICULTY = 8;
/**
 * Wrong taps allowed before the trial closes.
 *
 * Generous relative to the grid — six cells and a budget of four means a child can be wrong about
 * most of the board and still finish. The budget exists so the trial can END, not to punish
 * searching: without one, tapping every cell solves any trial and the score stops meaning anything.
 */
export function wrongTapBudget(stimulus) {
    return stimulus.cols * stimulus.rows - stimulus.differences.length + 1;
}
export function generateDifference(task, formId, trialIndex, difficulty) {
    if (difficulty < DIFF_MIN_DIFFICULTY || difficulty > DIFF_MAX_DIFFICULTY) {
        throw new Error(`difficulty ${difficulty} out of range for ${task.id}`);
    }
    const { count, kinds } = recipeFor(difficulty);
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    /**
     * Every cell distinct when the level uses swaps.
     *
     * A swap between two cells holding the same token changes nothing, and the trial would ship with
     * fewer differences than it claims — one the child can never finish. Drawing distinct tokens
     * throughout is cheaper than checking for it afterwards, and six distinct tokens out of
     * twenty-four is never a tight draw.
     */
    const distinct = kinds.includes("swap");
    const left = distinctTokens(DIFF_CELLS, distinct, rand);
    // Which cells change. Drawn without replacement, so `differences.length` is always `count` —
    // a repeat would silently make an easier trial than the difficulty claims.
    const pool = Array.from({ length: DIFF_CELLS }, (_, i) => i);
    for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    const right = [...left];
    const differences = [];
    let next = 0;
    for (const kind of kinds) {
        if (kind === "swap") {
            const a = pool[next++];
            const b = pool[next++];
            if (a === undefined || b === undefined)
                break;
            right[a] = left[b];
            right[b] = left[a];
            differences.push(a, b);
            continue;
        }
        const cell = pool[next++];
        if (cell === undefined)
            break;
        const original = left[cell];
        right[cell] =
            kind === "shape"
                ? packToken(otherThan(tokenShape(original), SHAPES, rand), tokenColor(original))
                : packToken(tokenShape(original), otherThan(tokenColor(original), COLORS, rand));
        differences.push(cell);
    }
    if (differences.length !== count) {
        // The recipes are written so this cannot happen; throwing beats emitting a trial whose
        // difficulty label does not match what is on screen.
        throw new Error(`recipe for difficulty ${difficulty} produced ${differences.length} differences, not ${count}`);
    }
    return {
        cols: DIFF_COLS,
        rows: DIFF_ROWS,
        left,
        right,
        differences: differences.sort((a, b) => a - b),
    };
}
/** `count` tokens, all different when `distinct` — needed by the swap levels. */
function distinctTokens(count, distinct, rand) {
    const draw = () => packToken(Math.floor(rand() * SHAPES), Math.floor(rand() * COLORS));
    if (!distinct)
        return Array.from({ length: count }, draw);
    const chosen = new Set();
    let guard = 0;
    while (chosen.size < count && guard++ < 500)
        chosen.add(draw());
    if (chosen.size < count)
        throw new Error(`could not draw ${count} distinct tokens`);
    return [...chosen];
}
/**
 * A value in [0, range) that is not `current`.
 *
 * Drawn from the remaining values rather than by re-rolling until it differs: a re-roll loop can
 * in principle spin, and more importantly a "difference" that came out identical would produce a
 * trial with fewer differences than it reports — one the child can never finish.
 */
function otherThan(current, range, rand) {
    const offset = 1 + Math.floor(rand() * (range - 1));
    return (current + offset) % range;
}
/** Found every difference, without exhausting the wrong-tap budget. */
export function scoreDifferenceTrial(stimulus, found, wrongTaps) {
    const need = new Set(stimulus.differences);
    for (const cell of found)
        need.delete(cell);
    return need.size === 0 && wrongTaps <= wrongTapBudget(stimulus);
}
//# sourceMappingURL=difference.js.map